// src/hooks/sync/useGlobalChallengeSync.js
// ─────────────────────────────────────────────────────────────────────────────
//  Global challenge auto-progress hook.
//
//  Watches every activity store across the entire app and fires autoCheckIn()
//  whenever relevant data changes — no individual page edits needed.
//
//  This is the SINGLE source of truth for cross-page auto-progress. It runs
//  inside Layout so it's active regardless of which page the user is on.
//
//  STRATEGY: snapshot the previous state. When a count increases (new prayer
//  logged, new chapter read, etc.) fire the matching habitId. Intentionally
//  only fires on *increases* (not on mount) to avoid re-firing for existing
//  data every time the app opens.
//
//  EXTRACTED from Layout.jsx (was lines ~1111-1198).
//  Layout.jsx now imports and calls: useGlobalChallengeSync()
//
// ── BUG FIXES IN THIS VERSION ─────────────────────────────────────────────────
//
//  FIX 🔴 BUG-D1: bibleReadToday was read from habitFlags but was NEVER
//    written by any page — Bible reading is tracked in readingPlanData /
//    STORE_BIBLE_READING_HISTORY, not in habitFlags.
//    Fix: also watch bibleReadingHistory store (completed chapter count) for
//    increases, and watch habitFlags.bibleReadToday which is now written by
//    handlers.js BIBLE_READING_ADVANCED and BIBLE_STUDY_COMPLETED events.
//
//  FIX 🔴 BUG-D2: Journal snapshot only checked hasEdits and practiceNotes.
//    The 5P fields (passage, principle, practice, praise) were completely
//    ignored — journal challenges never triggered for 5P-only entries.
//    Fix: snapshot now detects any non-empty 5P field as a journal edit.
//
//  FIX 🔴 BUG-D4: Contact auto-trigger used people.filter(p => p.date === todayKey).
//    Contacts are stored with `createdAt` ISO string (not a `date` field) so
//    the filter always returned 0 and contact challenges never auto-triggered.
//    Fix: filter on createdAt.startsWith(todayKey) instead.
//
//  FIX 🔴 BUG-D5: titheRecordedToday was never included in the snapshot or
//    the comparison block, so recording a tithe never triggered habit_expense.
//    Fix: titheRecordedToday added to snapshot and comparison.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef } from 'react';
import { useSyncStore }      from '@/hooks/sync/useSyncStore';
import { useAutoCheckIn }    from '@/hooks/sync/useAutoCheckIn';
import { formatDate }        from '@/utils/helpers';
import {
  STORE_PRAYER_CHECKINS,
  STORE_SERMON_NOTES,
  STORE_DISCIPLESHIP_ATTENDANCE,
  STORE_EXPENSES,
  STORE_PEOPLE_CONTACTS,
  STORE_DAILY_ENTRIES,
  STORE_BIBLE_STUDY_PROGRESS,
  STORE_HABIT_FLAGS,
  STORE_BIBLE_READING_HISTORY,
} from '@/lib/storeKeys';

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useGlobalChallengeSync() {
  const { autoCheckIn } = useAutoCheckIn();

  // ── All activity stores ──────────────────────────────────────────────────
  const [prayerCheckins, , prayerReady] = useSyncStore(STORE_PRAYER_CHECKINS, {});
  const [sermonNotes, , sermonReady] = useSyncStore(STORE_SERMON_NOTES, []);
  const [discipleshipAttendance, , attendanceReady] = useSyncStore(STORE_DISCIPLESHIP_ATTENDANCE, []);
  const [expenses, , expensesReady] = useSyncStore(STORE_EXPENSES, []);
  const [peopleContacts, , contactsReady] = useSyncStore(STORE_PEOPLE_CONTACTS, []);
  const [dailyEntries, , dailyReady] = useSyncStore(STORE_DAILY_ENTRIES, {});
  const [bibleStudyProgress, , bibleStudyReady] = useSyncStore(STORE_BIBLE_STUDY_PROGRESS, {});
  const [habitFlags, , flagsReady] = useSyncStore(STORE_HABIT_FLAGS, {});
  // FIX BUG-D1: watch bible reading history so completed chapters trigger
  // the bible challenge even when the user reads without emitting an event.
  const [bibleReadingHistory, , bibleHistoryReady] = useSyncStore(STORE_BIBLE_READING_HISTORY, []);

  // Stores load asynchronously and start at their defaults. Wait for all of them
  // so the first snapshot reflects real data; otherwise loaded history counts as
  // an "increase" and fires check-ins on every app open.
  const allReady = prayerReady && sermonReady && attendanceReady && expensesReady &&
    contactsReady && dailyReady && bibleStudyReady && flagsReady && bibleHistoryReady;

  // Snapshot ref — null sentinel means first-run (don't fire on mount)
  const prevRef = useRef(null);

  useEffect(() => {
    const todayKey = formatDate(new Date());

    const snapshot = () => {
      // ── Journal: detect ANY content written today ─────────────────────────
      // FIX BUG-D2: previously only checked hasEdits and practiceNotes.
      // The 5P fields (passage, principle, practice, praise) were ignored —
      // journal challenges never auto-triggered for 5P-only entries.
      const todayEntry = dailyEntries?.[todayKey];
      const journalHasContent = !!(
        todayEntry?.hasEdits              ||
        todayEntry?.practiceNotes?.trim() ||
        todayEntry?.passage?.trim()       ||
        todayEntry?.principle?.trim()     ||
        todayEntry?.practice?.trim()      ||
        todayEntry?.praise?.trim()        ||
        todayEntry?.praises?.trim()       ||
        todayEntry?.prayer?.trim()
      );

      // ── Contacts: use createdAt not date ─────────────────────────────────
      // FIX BUG-D4: contacts use `createdAt` (ISO string) not `date`.
      // The old filter p.date === todayKey always returned 0.
      const contactCountToday = Array.isArray(peopleContacts)
        ? peopleContacts.filter(p =>
            (p.createdAt && p.createdAt.startsWith(todayKey)) ||
            p.date === todayKey   // keep legacy field as fallback
          ).length
        : 0;

      // ── Bible reading: completed chapters + habitFlags ────────────────────
      // FIX BUG-D1: watch completed chapter count from bibleReadingHistory,
      // PLUS habitFlags.bibleReadToday which is now written by handlers.js
      // BIBLE_READING_ADVANCED and BIBLE_STUDY_COMPLETED events.
      const bibleChaptersTotal = Array.isArray(bibleReadingHistory) ? bibleReadingHistory.length : 0;
      const bibleReadTodayFlag = habitFlags?.bibleReadToday === todayKey ? 1 : 0;

      // ── Bible study: count completed lessons ─────────────────────────────
      const bibleStudyDone = (() => {
        const bsp = bibleStudyProgress;
        if (!bsp || typeof bsp !== 'object') return 0;
        return Object.values(bsp).reduce((acc, book) => {
          if (!book || typeof book !== 'object') return acc;
          return acc + Object.values(book).filter(
            v => v === true || (v && typeof v === 'object' && v.completed)
          ).length;
        }, 0);
      })();

      return {
        day:                todayKey,
        prayedToday:        Object.keys((prayerCheckins?.[todayKey]) || {}).length,
        sermonCount:        Array.isArray(sermonNotes) ? sermonNotes.length : 0,
        attendanceCount:    Array.isArray(discipleshipAttendance) ? discipleshipAttendance.length : 0,
        expenseCountToday:  Array.isArray(expenses) ? expenses.filter(e => e.date === todayKey).length : 0,
        contactCountToday,
        journalHasContent:  journalHasContent ? 1 : 0,
        bibleChaptersTotal,
        bibleReadTodayFlag,
        bibleStudyDone,
        // habitFlag mirrors — written by trigger engine handlers
        sermonSavedToday:   habitFlags?.sermonSavedToday   === todayKey ? 1 : 0,
        discipleMetToday:   habitFlags?.discipleMetToday   === todayKey ? 1 : 0,
        contactedToday:     habitFlags?.contactedToday     === todayKey ? 1 : 0,
        journalEditedToday: habitFlags?.journalEditedToday === todayKey ? 1 : 0,
        // FIX BUG-D5: titheRecordedToday was missing — tithes never triggered habit_expense.
        titheRecordedToday: habitFlags?.titheRecordedToday === todayKey ? 1 : 0,
      };
    };

    // Wait until every store has finished its async load.
    if (!allReady) return;

    if (prevRef.current === null) {
      // First run — initialise without firing so existing data
      // doesn't re-fire on every app open.
      prevRef.current = snapshot();
      return;
    }

    const curr = snapshot();
    const prevSnap = prevRef.current;
    // Day rolled over while the app stayed open: today-scoped counters restart at zero
    // so the first action of the new day still counts as an increase.
    const prev = prevSnap.day === todayKey
      ? prevSnap
      : {
          ...prevSnap,
          prayedToday: 0, expenseCountToday: 0, contactCountToday: 0,
          journalHasContent: 0, bibleReadTodayFlag: 0,
          sermonSavedToday: 0, discipleMetToday: 0, contactedToday: 0,
          journalEditedToday: 0, titheRecordedToday: 0,
        };
    prevRef.current = curr;

    // ── Prayer: more prayer check-ins today → habit_pray ─────────────────────
    if (curr.prayedToday > prev.prayedToday)
      autoCheckIn('habit_pray');

    // ── Sermon notes: new note saved → habit_sermon ───────────────────────────
    if (curr.sermonCount > prev.sermonCount || curr.sermonSavedToday > prev.sermonSavedToday)
      autoCheckIn('habit_sermon');

    // ── Discipleship attendance: new session recorded → habit_disciple ─────────
    if (curr.attendanceCount > prev.attendanceCount || curr.discipleMetToday > prev.discipleMetToday)
      autoCheckIn('habit_disciple');

    // ── Expense: new expense logged today → habit_expense ─────────────────────
    // FIX BUG-D5: also fires for tithes (titheRecordedToday) since tithes
    // are a stewardship action and map to the same challenge category.
    if (
      curr.expenseCountToday   > prev.expenseCountToday   ||
      curr.titheRecordedToday  > prev.titheRecordedToday
    )
      autoCheckIn('habit_expense');

    // ── Contact: new person contacted today → habit_contact ───────────────────
    // FIX BUG-D4: now uses createdAt-based contactCountToday
    if (curr.contactCountToday > prev.contactCountToday || curr.contactedToday > prev.contactedToday)
      autoCheckIn('habit_contact');

    // ── Journal: new journal write today → habit_journal ──────────────────────
    // FIX BUG-D2: journalHasContent now covers all 5P fields
    if (curr.journalHasContent > prev.journalHasContent || curr.journalEditedToday > prev.journalEditedToday)
      autoCheckIn('habit_journal');

    // ── Bible: reading history grew, habitFlag set, or study completed → habit_bible
    // FIX BUG-D1: bibleChaptersTotal increase now also triggers the check-in
    if (
      curr.bibleReadTodayFlag  > prev.bibleReadTodayFlag  ||
      curr.bibleChaptersTotal  > prev.bibleChaptersTotal  ||
      curr.bibleStudyDone      > prev.bibleStudyDone
    )
      autoCheckIn('habit_bible');

  }, [
    prayerCheckins, sermonNotes, discipleshipAttendance,
    expenses, peopleContacts, dailyEntries, bibleStudyProgress,
    habitFlags, bibleReadingHistory, autoCheckIn, allReady,
  ]);
}